if (!customElements.get('extra-members-form')) {
    customElements.define(
        'extra-members-form',
        class ExtraMembersForm extends HTMLElement {
            constructor() {
                super();
                this.sectionId = this.dataset.sectionId;
                this.memberCost = parseFloat(this.dataset.memberCost) || 21;
                this.extraVariantId = this.dataset.extraVariantId;
                this.checkbox = this.querySelector(`#ExtraMembers-checkbox-${this.sectionId}`);
                this.quantityInput = this.querySelector(`#ExtraMemberQuantity-${this.sectionId}`);
                this.costDisplay = this.querySelector('[data-extra-members-cost]');
                this.priceLabel = this.querySelector('[data-extra-members-price]');
                this.isAddingExtra = false;

                // NEW: refs to the hidden inputs injected for Buy Now
                this.itemIdInput = null;
                this.itemQtyInput = null;

                this.variantMap = this.loadVariantMap();

                this.checkbox?.addEventListener('change', () => this.onToggle());
                this.quantityInput?.addEventListener('change', () => this.updateCost());
                this.quantityInput?.addEventListener('input', () => this.updateCost());

                this.onToggle();
            }

            connectedCallback() {
                this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                    if (event.source !== 'product-form' || this.isAddingExtra) return;

                    const quantity = this.getQuantity();
                    if (!this.checkbox?.checked || quantity <= 0 || !this.extraVariantId) return;

                    // Variant ID of the book that was just added
                    const variantId = event.productVariantId;
                    // Cart API response from the add
                    const cartData = event.cartData;
                    // Try to get product ID from the response
                    let productId = cartData?.product_id;
                    // If response has items array (multi-item add)
                    if (!productId && cartData?.items?.length) {
                    const line = cartData.items.find(
                        (item) => String(item.variant_id) === String(variantId)
                    );
                    productId = line?.product_id;
                    }

                    this.addExtraToCart(quantity,productId,variantId);
                });

                this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                    if (event.data?.sectionId !== this.sectionId) return;

                    const variant = event.data?.variant;
                    if (!variant || !this.variantMap) return;

                    const entry = this.variantMap[String(variant.id)];
                    if (!entry) return;

                    this.extraVariantId = String(entry.extra_variant_id);
                    this.memberCost = parseFloat(entry.member_cost) || this.memberCost;

                    this.dataset.extraVariantId = this.extraVariantId;
                    this.dataset.memberCost = this.memberCost;

                    if (this.priceLabel) {
                        this.priceLabel.textContent = `($${this.memberCost} per member)`;
                    }

                    this.updateCost();

                    // NEW: keep Buy Now hidden inputs pointed at the new variant
                    if (this.checkbox?.checked) {
                        this.removeHiddenInputs();
                        this.addHiddenInputs();
                    }
                });
            }

            disconnectedCallback() {
                this.cartUpdateUnsubscriber?.();
                this.variantChangeUnsubscriber?.();
                this.removeHiddenInputs(); // NEW
            }

            loadVariantMap() {
                const mapEl = document.getElementById(`ExtraMembersMap-${this.sectionId}`);
                if (!mapEl) return null;
                try {
                    return JSON.parse(mapEl.textContent);
                } catch (error) {
                    console.error('Could not parse extra members variant map', error);
                    return null;
                }
            }

            getQuantity() {
                return this.checkbox?.checked ? parseInt(this.quantityInput?.value, 10) || 0 : 0;
            }

            onToggle() {
                const isChecked = this.checkbox?.checked;

                if (this.quantityInput) {
                    this.quantityInput.disabled = !isChecked;

                    if (isChecked) {
                        if (!this.quantityInput.value || parseInt(this.quantityInput.value, 10) < 1) {
                            this.quantityInput.value = '1';
                        }
                    } else {
                        this.quantityInput.value = '0';
                    }
                }

                // NEW: add/remove Buy Now hidden inputs based on checkbox state
                if (isChecked) {
                    this.addHiddenInputs();
                } else {
                    this.removeHiddenInputs();
                }

                this.updateCost();
            }

            updateCost() {
                if (this.costDisplay) {
                    const total = this.getQuantity() * this.memberCost;
                    this.costDisplay.textContent = `$${total.toFixed(2)}`;
                }

                // NEW: keep the Buy Now hidden quantity input in sync
                if (this.itemQtyInput) {
                    this.itemQtyInput.value = this.getQuantity();
                }
            }

            // --- NEW: Buy Now support ---
            // shopify-accelerated-checkout reads shopify-additional-items[N][id]/[quantity]
            // directly off the form DOM, so we inject/remove them here.

            getForm() {
                return this.closest('product-info')?.querySelector('form[data-type="add-to-cart-form"]')
                    || document.getElementById(`product-form-${this.sectionId}`);
            }

            addHiddenInputs() {
                const form = this.getForm();
                if (!form || !this.extraVariantId) return;

                if (this.itemIdInput && this.itemQtyInput) {
                    this.itemIdInput.value = this.extraVariantId;
                    this.itemQtyInput.value = this.getQuantity();
                    return;
                }

                this.itemIdInput = document.createElement('input');
                this.itemIdInput.type = 'hidden';
                this.itemIdInput.name = 'shopify-additional-items[1][id]';
                this.itemIdInput.value = this.extraVariantId;

                this.itemQtyInput = document.createElement('input');
                this.itemQtyInput.type = 'hidden';
                this.itemQtyInput.name = 'shopify-additional-items[1][quantity]';
                this.itemQtyInput.value = this.getQuantity();

                form.appendChild(this.itemIdInput);
                form.appendChild(this.itemQtyInput);
            }

            removeHiddenInputs() {
                this.itemIdInput?.remove();
                this.itemQtyInput?.remove();
                this.itemIdInput = null;
                this.itemQtyInput = null;
            }

            // --- Existing Add to Cart (AJAX) logic — unchanged ---

            addExtraToCart(quantity,product_id = null,variant_id = null) {
                this.isAddingExtra = true;

                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData();
                formData.append('id', this.extraVariantId);
                formData.append('quantity', quantity);
                formData.append('properties[ProductId]', product_id);
                formData.append('properties[VariantId]', variant_id);

                const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
                if (cart) {
                    formData.append(
                        'sections',
                        cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                }

                config.body = formData;

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            console.error(response.description || response.message);
                            return;
                        }

                        if (cart) {
                            cart.renderContents(response);
                        }

                        publish(PUB_SUB_EVENTS.cartUpdate, {
                            source: 'extra-members-form',
                            cartData: response,
                        });
                    })
                    .catch((error) => {
                        console.error(error);
                    })
                    .finally(() => {
                        this.isAddingExtra = false;
                    });
            }
        }
    );
}